import type { EntryPointKey } from "../config/referral";
import notoRegular from "@osago/ui/fonts/NotoSansRegular.woff2";
import notoBold from "@osago/ui/fonts/NotoSansBold.woff2";
import notoExtraBold from "@osago/ui/fonts/NotoSansExtraBold.woff2";

/**
 * Данные и оформление печатного макета A4 — то, что правится чаще всего.
 * Разметка живёт в features/referral/print/PrintPoster.
 *
 * PRINT_PRODUCTS — тексты по продуктам, DISCLAIMER — юридическая часть,
 * PRINT_CSS — размеры, цвета и отступы листа.
 */

export interface PrintProduct {
  brand: string;
  headline: string;
  accent: string;
  promoTitle: string;
  promoAccent: string;
  bullets: string[];
}

export const PRINT_PRODUCTS: Record<"osago" | "pkasko" | "mortgage", PrintProduct> = {
  osago: {
    brand: "ОСАГО онлайн",
    headline: "Экономь на ОСАГО!",
    accent: "Выбирай из лучших!",
    promoTitle: "Проверенная ",
    promoAccent: "выгода!",
    bullets: [
      "Сравнение предложений страховых компаний",
      "Оформление онлайн за несколько минут",
      "Официальные полисы страховых компаний",
    ],
  },
  pkasko: {
    brand: "ПКаско онлайн",
    headline: "Защити свой автомобиль!",
    accent: "ПКаско — просто и выгодно!",
    promoTitle: "Проверенная ",
    promoAccent: "защита!",
    bullets: [
      "Продукт ПКаско от страховой компании Гелиос",
      "Оформление онлайн за несколько минут",
      "Официальный полис страховой компании",
    ],
  },
  mortgage: {
    brand: "Ипотечное страхование",
    headline: "Страхуй ипотеку выгодно!",
    accent: "Оформляй онлайн!",
    promoTitle: "Проверенная ",
    promoAccent: "выгода!",
    bullets: [
      "Полис для ипотеки, который примет ваш банк",
      "Оформление онлайн за несколько минут",
      "Официальные полисы страховых компаний",
    ],
  },
};

export function getPrintProduct(entryPoint: EntryPointKey): PrintProduct {
  if (entryPoint === "pkasko") return PRINT_PRODUCTS.pkasko;
  if (entryPoint === "mortgage") return PRINT_PRODUCTS.mortgage;
  return PRINT_PRODUCTS.osago;
}

export const DISCLAIMER =
  "Реклама. ООО «БЛОКЧЕЙН», ИНН 6234181385. Услуги по сравнению и подбору страховых " +
  "продуктов. Оформление происходит на сайтах страховых компаний. Подробнее на osagotop.ru";

/**
 * @font-face для окна печати. Оно не наследует стили приложения, поэтому без
 * этих правил "Noto Sans" в PRINT_CSS не находится и лист печатается системным
 * шрифтом — не тем, что показан в превью.
 *
 * Веса ровно те, что использует макет: 400 — текст, 700 — бренд и маркеры,
 * 800 — слоган и заголовок промо-блока.
 *
 * font-display: block, а не swap: макет уходит в печать один раз, подменить
 * шрифт после отрисовки уже некому.
 */
export const PRINT_FONTS_CSS = (
  [
    [400, notoRegular],
    [700, notoBold],
    [800, notoExtraBold],
  ] as const
)
  .map(
    ([weight, src]) => `@font-face { font-family: "Noto Sans";
  src: url("${new URL(src, location.href).href}") format("woff2");
  font-weight: ${weight}; font-style: normal; font-display: block; }`,
  )
  .join("\n");

export const PRINT_CSS = `
.a4 { width: 793px; height: 1122px; margin: 0; padding: 48px 64px; background: #fff;
  font-family: "Noto Sans", system-ui, -apple-system, sans-serif; color: #111827;
  display: flex; flex-direction: column; align-items: center; text-align: center; box-sizing: border-box;
  /* Без exact принтер работает в режиме economy: гасит акцентный #457be7 и
     выбрасывает заливку промо-блока. Свойство наследуется — хватает одного раза. */
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.a4-rule { width: 100%; height: 2px; background: #111827; flex: none; margin: 0; }
.a4-brand { display: flex; align-items: center; gap: 12px; margin: 36px 0 0; }
.a4-brand img { height: 44px; width: auto; }
.a4-brand span { font-size: 26px; font-weight: 700; color: #457be7; }
.a4-headline { margin: 34px 0 0; font-size: 52px; line-height: 1.15; font-weight: 800; }
.a4-headline em { font-style: normal; color: #457be7; }
.a4-qr { margin: 40px 0 0; width: 300px; height: 300px; }
.a4-scan { margin: 18px 0 0; font-size: 17px; color: #4b5563; }
.a4-link { margin: 8px 0 0; font-family: ui-monospace, Menlo, Consolas, monospace;
  font-size: 13px; color: #457be7; word-break: break-all; max-width: 620px; }
.a4-promo { margin: 38px 0 0; width: 100%; background: #f0f5ff; border-radius: 16px;
  padding: 26px 32px; box-sizing: border-box; }
.a4-promo-title { margin: 0; font-size: 34px; font-weight: 800; }
.a4-promo-title em { font-style: normal; color: #457be7; }
.a4-promo-list { margin: 12px 0 0; padding: 0; list-style: none; font-size: 16px;
  color: #2d2d2d; line-height: 1.7; }
.a4-promo-list li { margin: 0; }
.a4-promo-list li::before { content: "\\2014\\00a0"; color: #457be7; font-weight: 700; }
.a4-spacer { flex: 1; }
.a4-disclaimer { display: flex; align-items: center; gap: 12px; margin: 0 0 14px; max-width: 640px;
  font-size: 10px; color: #6d6b6b; line-height: 1.5; text-align: left; }
.a4-badge { flex: none; width: 30px; height: 30px; border: 1.5px solid #6d6b6b; border-radius: 50%;
  line-height: 27px; text-align: center; font-weight: 700; font-size: 11px; box-sizing: border-box; }
`.trim();
